import { registerDestructor } from "@ember/destroyable";
import type Owner from "@ember/owner";
import type RouterService from "@ember/routing/router-service";
import Service, { service } from "@ember/service";
import { isTesting } from "@embroider/macros";
import { tracked } from "@glimmer/tracking";
import { runTask } from "ember-lifeline";
import ENV from "parog-web/config/environment";

/**
 * Detects when a new version of the front has been deployed and reloads the
 * page on a set of routes where it is safe to do so.
 *
 * It regularly fetches a VERSION.txt file served at the root of the server.
 * This file holds the version of the deployed app. When its content differs
 * from the version baked into the running app, a new version is available.
 *
 * VERSION.txt is generated at build time.
 *
 * This used to rely on the (unmaintained, Ember v1) ember-cli-new-version addon
 * and is now a self-contained native service.
 */

export default class NewVersionService extends Service {
  @service declare router: RouterService;

  @tracked latestVersion?: string;

  autoreloadRoutes = new Set([
    "login.index",
    "login.medic-password",
    "medics-web.protected.index.alerts-and-notifications",
    "medics-web.protected.index.tasks-and-consultations",
    "medics-web.protected.index.patient-list",
    "login.patient-link",
    "patient-web.protected.dashboard.index"
  ]);

  get currentVersion(): string {
    return this.newVersionConfig.currentVersion;
  }

  get isNewVersionAvailable(): boolean {
    return this.hasFetchedVersion && this.latestVersion !== this.currentVersion;
  }

  protected get newVersionConfig() {
    return ENV.newVersion;
  }

  private testingRunCount = 0;

  /** Whether a version has been fetched from the server at least once. */
  private get hasFetchedVersion(): boolean {
    return this.latestVersion !== undefined;
  }

  private get url(): string {
    return `${ENV.rootURL}${this.newVersionConfig.versionFileName}`;
  }

  constructor(owner: Owner) {
    super(owner);

    this.router.on("routeDidChange", this.automaticReloadIfNeeded);

    registerDestructor(this, () => {
      this.router.off("routeDidChange", this.automaticReloadIfNeeded);
    });

    const isDev = ENV.environment === "development";

    if (
      (isTesting() && !this.newVersionConfig.isEnabledInTests) ||
      (isDev && !this.newVersionConfig.isEnabledInDev)
    )
      return;

    if (this.newVersionConfig.firstCheckInterval > 0) {
      runTask(
        this,
        () => this.checkVersion(),
        this.newVersionConfig.firstCheckInterval
      );
    } else {
      void this.checkVersion();
    }
  }

  protected onNewVersion(): void {
    this.automaticReloadIfNeeded();
  }

  protected reload(): void {
    window.location.reload();
  }

  private automaticReloadIfNeeded = (): void => {
    if (!this.isNewVersionAvailable) return;

    const currentRouteName = this.router.currentRouteName;

    if (currentRouteName && this.autoreloadRoutes.has(currentRouteName))
      this.reload();
  };

  private async checkVersion(): Promise<void> {
    try {
      const response = await fetch(`${this.url}?_=${Date.now()}`);

      if (!response.ok) throw new Error(response.statusText);

      const text = await response.text();
      this.latestVersion = text.trim() || undefined;

      if (this.isNewVersionAvailable) this.onNewVersion();
    } catch (error) {
      if (!isTesting()) console.error(error);
    }

    this.scheduleNextCheck();
  }

  /**
   * Counts one poll iteration and reports whether the testing cap has been
   * exceeded, so the loop cannot run forever in tests.
   */
  private hasReachedTestingPollLimit(): boolean {
    if (!isTesting()) return false;

    return ++this.testingRunCount > this.newVersionConfig.maxCountInTesting;
  }

  private scheduleNextCheck(): void {
    if (this.hasReachedTestingPollLimit()) return;

    runTask(
      this,
      () => this.checkVersion(),
      this.newVersionConfig.updateInterval
    );
  }
}

declare module "@ember/service" {
  interface Registry {
    "new-version": NewVersionService;
  }
}
